// NOTE: Code assumes you already have working routing and `/admin/company/:id` page.

import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { Button, Form, Input, message, Modal, Popconfirm, Space, Table, Tooltip } from 'antd';
import CustomSearch from '../../components/search/CustomSearch';
import {
    DeleteOutlined,
    LoadingOutlined,
    StopOutlined,
} from "@ant-design/icons";
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import RightArrowIcon from '../../components/icons/RightArrowIcon';

const { TextArea } = Input;

const ProfessionalCompanies = () => {
    const user_role = useSelector((state) => state.auth?.userInfo?.role);
    const isSuperadmin = user_role?.toLowerCase() === "superadmin";
    const [professionals, setProfessionals] = useState([]);
    const [companySearch, setCompanySearch] = useState("");
    const [deactivateModal, setDeactivateModal] = useState(false);
    const [companyId, setCompanyId] = useState(null);
    const [modalText, setModalText] = useState("");
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [deletingCompanyId, setDeletingCompanyId] = useState(null);
    const [activeOpen, setActiveOpen] = useState(false);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/professional-companies/');
            setProfessionals(res.data.data || []);
            setLoading(false);
        } catch (err) {
            setLoading(false);
            message.error("Failed to fetch data");
        }
    };

    const handleCancel = () => {
        setDeactivateModal(false);
        setActiveOpen(false);
        form.resetFields();
    };

    const formatIndianDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const showDeactivateModal = (id) => {
        setCompanyId(id);
        setModalText("Are you sure you want to deactivate this company?");
        setDeactivateModal(true);
    };

    const showActivateModal = (id) => {
        setCompanyId(id);
        setModalText("Are you sure you want to activate this company?");
        setActiveOpen(true);
    };

    const handleCompanyStatus = (action) => {
        const payload = {
            active: action === 'activate',
            ...(action === 'deactivate' && { reason: form.getFieldValue('reason') })
        };

        axiosInstance.put(`/company/${companyId}/active/`, payload)
            .then(() => {
                message.success(`Company ${action}d successfully`);
                setDeactivateModal(false);
                setActiveOpen(false);
                form.resetFields();
                fetchCompanies();
            })
            .catch(() => message.error("Something went wrong"));
    };

    const handleCompanySearch = (value) => {
        setCompanySearch(value || "");
    };

    const filteredProfessionals = useMemo(() => {
        const query = companySearch.trim().toLowerCase();
        if (!query) return professionals || [];

        return (professionals || []).reduce((acc, professional) => {
            const ownName = (professional?.own_company?.name || "").toLowerCase();
            const createdCompanies = professional?.created_companies || [];
            const ownMatches = ownName.includes(query);
            const matchingCreated = createdCompanies.filter((companyItem) =>
                (companyItem?.name || "").toLowerCase().includes(query)
            );

            if (!ownMatches && matchingCreated.length === 0) {
                return acc;
            }

            acc.push({
                ...professional,
                created_companies: ownMatches ? createdCompanies : matchingCreated,
            });
            return acc;
        }, []);
    }, [professionals, companySearch]);

    const handleStopEmailTask = async (companyId) => {
        if (!isSuperadmin) {
            message.error("Only superadmin can stop email tasks");
            return;
        }
        if (!companyId) {
            message.error("Company id is required");
            return;
        }
        try {
            const response = await axiosInstance.post("/company/stop-pending-auto-email/", {
                company_id: companyId,
            });
            message.success(response?.data?.message || "Email task stopped for this company");
        } catch (error) {
            message.error(error?.response?.data?.error || "Failed to stop email task");
        }
    };

    const handleDeleteCompany = async (companyId) => {
        if (!isSuperadmin) {
            message.error("Only superadmin can delete a company");
            return;
        }
        if (!companyId) {
            message.error("Company id is required");
            return;
        }
        try {
            setDeletingCompanyId(companyId);
            const response = await axiosInstance.post("/company/delete/", {
                company_id: companyId,
            }, { timeout: 120000 });
            message.success(response?.data?.message || "Company deleted successfully");
            fetchCompanies();
        } catch (error) {
            message.error(error?.response?.data?.error || "Failed to delete company");
        } finally {
            setDeletingCompanyId(null);
        }
    };

    const companyActionColumn = isSuperadmin ? [{
        title: "Action",
        key: "company_actions",
        render: (text, record) => {
            const targetCompanyId = record?.own_company?.id || record?.id;
            const isProfessionalOwnCompany = Boolean(record?.own_company?.id);
            return (
                <Space size="small" wrap className="flex items-center">
                    <Tooltip title="Stop Email Task">
                        <Popconfirm
                            title="Stop Email Task"
                            description="Stop 7-day auto emails for this company?"
                            onConfirm={() => handleStopEmailTask(targetCompanyId)}
                            okText="Stop"
                            cancelText="Cancel"
                        >
                            <Button
                                type="primary"
                                shape="circle"
                                size="small"
                                danger
                                icon={<StopOutlined />}
                            />
                        </Popconfirm>
                    </Tooltip>
                    <Tooltip title="Delete Company">
                        <Popconfirm
                            title="Delete Company"
                            description={
                                isProfessionalOwnCompany
                                    ? "Are you sure you want to delete this company? Data will not exist after deleting this company. All companies created by this professional will also be deleted."
                                    : "Are you sure you want to delete this company? Data will not exist after deleting this company."
                            }
                            onConfirm={() => handleDeleteCompany(targetCompanyId)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true, loading: deletingCompanyId === targetCompanyId }}
                        >
                            <Button
                                shape="circle"
                                size="small"
                                danger
                                loading={deletingCompanyId === targetCompanyId}
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            );
        }
    }] : [];

    const expandedRowRender = (record) => {
        const createdCompanies = record.created_companies || [];

        if (!createdCompanies.length) {
            return <p className="text-[12px] italic text-gray-500">No created companies.</p>;
        }

        const columns = [
            { title: 'Name', dataIndex: 'name' },
            { title: 'Admin Email', dataIndex: 'admin_email' },
            { title: 'Admin Contact', dataIndex: 'admin_contact_number' },
            { title: 'Valid From', dataIndex: 'valid_from', render: formatIndianDate },
            { title: 'Valid To', dataIndex: 'valid_to', render: formatIndianDate },
            // {
            //     title: 'Status',
            //     dataIndex: 'is_active',
            //     render: (val) => val ? 'Active' : 'Inactive'
            // },
            {
                title: 'Status',
                dataIndex: 'id',
                render: (id, row) => (
                    row.is_active ? (
                        <div className='inline-block rounded-full border-2 border-red px-6 pb-[6px] pt-2 bg-red-400 text-[10px] font-medium uppercase leading-normal text-black transition duration-150 ease-in-out hover:border-danger-600 hover:bg-neutral-500 hover:bg-opacity-10 hover:text-danger-600 focus:border-danger-600 focus:text-danger-600 focus:outline-none focus:ring-0 active:border-danger-700 active:text-danger-700 dark:hover:bg-neutral-100 dark:hover:bg-opacity-10'
                            onClick={() => showDeactivateModal(id)}>Deactivate</div>
                    ) : (
                        <div className="inline-block rounded-full border-2 border-success px-6 pb-[6px] pt-2 bg-green-500 text-[10px] font-medium uppercase leading-normal text-black transition duration-150 ease-in-out hover:border-success-600 hover:bg-neutral-500 hover:bg-opacity-10 hover:text-success-600 focus:border-success-600 focus:text-black focus:outline-none focus:ring-0 active:border-success-700 active:text-black-700 dark:hover:bg-neutral-100 dark:hover:bg-opacity-10"
                            onClick={() => showActivateModal(id)}>Activate</div>
                    )
                )
            },
            ...companyActionColumn,
            {
                title: 'View',
                dataIndex: 'id',
                render: (id) => (
                    <Link to={`/admin/company/${id}`}><RightArrowIcon /></Link>
                )
            },
        ];

        return <Table columns={columns} size='small' className='cursor-pointer text-[12px] [&_.ant-table-cell]:p-1 [&_.ant-table-cell]:text-[12px]' dataSource={createdCompanies} rowKey="id" pagination={false} scroll={{ x: "max-content" }} />;
    };

    const columns = [
        {
            title: 'Professional Name',
            dataIndex: 'professional_name',
        },
        {
            title: 'Professional Email',
            dataIndex: 'professional_email',
        },
        {
            title: 'Own Company',
            dataIndex: ['own_company', 'name'],
        },
        {
            title: 'Contact No.',
            dataIndex: ['own_company', 'admin_contact_number'],
        },
        {
            title: "Change Status",
            dataIndex: "is_active",
            key: "is_active",
            render: (text, record) => (
                <>
                    {record.is_active ? (
                        <div
                            // onClick={() => showADeactivateeModal(record.id)}
                            type="submit"
                            className="inline-block rounded-full border-2 border-red px-6 pb-[6px] pt-2 bg-red-400 text-[10px] font-medium uppercase leading-normal text-black transition duration-150 ease-in-out hover:border-danger-600 hover:bg-neutral-500 hover:bg-opacity-10 hover:text-danger-600 focus:border-danger-600 focus:text-danger-600 focus:outline-none focus:ring-0 active:border-danger-700 active:text-danger-700 dark:hover:bg-neutral-100 dark:hover:bg-opacity-10"
                        >
                            Deactivate
                        </div>
                    ) : (
                        <div
                            // onClick={() => showCompanyActiveModal(record.id)}
                            className="inline-block rounded-full border-2 border-success px-6 pb-[6px] pt-2 bg-green-500 text-[10px] font-medium uppercase leading-normal text-black transition duration-150 ease-in-out hover:border-success-600 hover:bg-neutral-500 hover:bg-opacity-10 hover:text-success-600 focus:border-success-600 focus:text-black focus:outline-none focus:ring-0 active:border-success-700 active:text-black-700 dark:hover:bg-neutral-100 dark:hover:bg-opacity-10"
                        >
                            Activate
                        </div>
                    )}
                </>
            ),
        },
        ...companyActionColumn,
        {
            title: 'View',
            dataIndex: ['own_company', 'id'],
            render: (id) => (
                <Link to={`/admin/company/${id}`}><RightArrowIcon /></Link>
            )
        },
    ];

    useEffect(() => {
        fetchCompanies();
    }, []);

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <div className='w-full sm:w-1/2 flex flex-col sm:flex-row gap-3 sm:gap-4'>
                    <h1 className="text-[15px] pt-1 font-bold sm:w-3/4 dark:text-white">Professional Companies</h1>
                    <CustomSearch onSearch={handleCompanySearch} onChange={handleCompanySearch} />

                </div>
            </div>
            {loading ? (
                <div className="text-center py-10">
                    <LoadingOutlined style={{ fontSize: 40 }} />
                </div>
            ) : (
                <Table
                    dataSource={filteredProfessionals}
                    columns={columns}
                    expandable={{ expandedRowRender }}
                    rowKey="professional_id"
                    size='small'
                    scroll={{ x: "max-content" }}
                    className="text-[12px] [&_.ant-table-cell]:p-1 [&_.ant-table-cell]:text-[12px]"
                />
            )}

            <Modal
                title="Deactivate Company"
                open={deactivateModal}
                onOk={() => form.submit()}
                onCancel={handleCancel}
                okText="Deactivate"
            >
                <Form form={form} onFinish={() => handleCompanyStatus("deactivate")}>
                    <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
                        <TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Activate Company"
                open={activeOpen}
                onOk={() => handleCompanyStatus("activate")}
                onCancel={handleCancel}
                okText="Activate"
            >
                <p>{modalText}</p>
            </Modal>
        </div>
    );
};

export default ProfessionalCompanies;